import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Message, Modal } from "semantic-ui-react";
import { toast } from "../..";
import { clearPasswordRecoveryToken } from "../../actions/authentificationActions";
import { resetPassword } from "../../api/users";
import { AppState } from "../../store/configureStore";
import "./ResetPasswordFirstLogin.css";

export const ResetPasswordFirstLogin = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const intl = useIntl();
    const user = useSelector((state: AppState) => state.user);
    const [sendingRequest, setSendingRequest] = useState(false);
    const [password, setPassword] = useState("");
    const [repeatedPassword, setRepeatedPassword] = useState("");
    const [errors, setErrors] = useState<string[]>([]);

    const handleResetPassword = async () => {
        setSendingRequest(true);
        try {
            await resetPassword({ token: user.passwordRecoveryToken, password: password });
            toast(intl.formatMessage({ id: "passwordChanged" }), true);
            dispatch(clearPasswordRecoveryToken());
            sessionStorage.setItem(
                "user",
                JSON.stringify({ ...user, passwordRecoveryToken: undefined })
            );
            history.push("/");
        } catch {
        } finally {
            setSendingRequest(false);
        }
    };

    useEffect(() => {
        if (password.length > 0) {
            if (password.length < 8)
                setErrors(x =>
                    !x.some(e => e === "passwordTooShort") ? x.concat("passwordTooShort") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordTooShort"));
            if (!/\d/.test(password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoNumber") ? x.concat("passwordNoNumber") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoNumber"));
            if (!/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoSpecialCharacter")
                        ? x.concat("passwordNoSpecialCharacter")
                        : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoSpecialCharacter"));
            if (!!password && !!repeatedPassword && password !== repeatedPassword)
                setErrors(x =>
                    !x.some(e => e === "passwordDontMatch") ? x.concat("passwordDontMatch") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordDontMatch"));
        } else setErrors([]);
    }, [password, repeatedPassword]);

    useEffect(() => {
        if (!!user.passwordRecoveryToken && location.pathname !== "/unauthorized")
            history.push("/unauthorized");
    }, [history, user.passwordRecoveryToken, location.pathname]);

    return (
        <Modal
            size="tiny"
            dimmer="blurring"
            open={!!user.passwordRecoveryToken}
            className="reset-password-modal"
            closeOnDimmerClick={false}
        >
            <Modal.Header id="reset-password-modal-container">
                {intl.formatMessage({ id: "passwordChangeHeader" })}
            </Modal.Header>
            <Modal.Content id="reset-password-modal-content">
                <Input
                    placeholder={intl.formatMessage({ id: "newPassword" })}
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                />
                <Input
                    placeholder={intl.formatMessage({ id: "repeatedPassword" })}
                    id="repeated-password"
                    type="password"
                    value={repeatedPassword}
                    onChange={e => setRepeatedPassword(e.target.value)}
                />
                <section className="password-reset-errors">
                    {errors.length > 0 && (
                        <Message
                            className="error-message"
                            icon="exclamation circle"
                            content={errors.map(e => (
                                <span key={e}>{intl.formatMessage({ id: e })}</span>
                            ))}
                        />
                    )}
                </section>
            </Modal.Content>
            <Modal.Actions id="reset-password-modal-container">
                <Button
                    id="reset-password-modal-button"
                    primary
                    content={intl.formatMessage({ id: "resetPassword" })}
                    loading={sendingRequest}
                    disabled={!password || !repeatedPassword || errors.length > 0 || sendingRequest}
                    onClick={handleResetPassword}
                />
            </Modal.Actions>
        </Modal>
    );
};
